import { CommonModule } from '@angular/common'
import { Component, inject, Input, ChangeDetectionStrategy } from '@angular/core'
import { MatIconButton } from '@angular/material/button'
import { MatIcon } from '@angular/material/icon'
import { MatSlideToggle } from '@angular/material/slide-toggle'
import { MatTooltip } from '@angular/material/tooltip'
import { RouterLink } from '@angular/router'
import { TranslateDirective, TranslatePipe, TranslateService } from '@ngx-translate/core'
import { Meeting, MeetingsService, MetricType } from '../meetings.service'
import { MatDialog } from '@angular/material/dialog'
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component'
import { formatCounterpartyNames } from '../utils'
import { InfoDialogComponent } from '../info-dialog/info-dialog.component'
import { differenceInHours } from 'date-fns'
import { firstValueFrom } from 'rxjs'
import { ToastService } from '../toast.service'
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu'
import { MetricsService } from '../metrics.service'

@Component({
  selector: 'app-upcoming-meeting-card',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    MatIcon,
    MatIconButton,
    TranslatePipe,
    TranslateDirective,
    MatTooltip,
    MatSlideToggle,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
  ],
  templateUrl: './upcoming-meeting-card.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './upcoming-meeting-card.component.scss',
})
export class UpcomingMeetingCardComponent {
  @Input() meeting!: Meeting

  meetingsService = inject(MeetingsService)
  dialog = inject(MatDialog)
  translateService = inject(TranslateService)
  toastService = inject(ToastService)
  metricsService = inject(MetricsService)
  excludeMeetingMutation = this.meetingsService.toggleExcludeMeetingMutation()
  joinNowMutation = this.meetingsService.joinNowMutation()
  forceDisableJoiningMutation = this.meetingsService.forceDisableJoiningMutation()
  deleteMeetingMutation = this.meetingsService.deleteMeetingMutation()

  get isMultipleAgendaType(): boolean {
    return this.meeting.agendaType === 'MULTIPLE'
  }

  get hasLostCalendarSync(): boolean {
    return this.meeting.calendarSyncLostAt !== null
  }

  forceDisableJoining(meeting: Meeting) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('MEETINGS.FORCE_DISABLE_DIALOG.TITLE'),
        description: this.translateService.instant('MEETINGS.FORCE_DISABLE_DIALOG.DESCRIPTION', { meetingName: meeting.name }),
        descriptionClass: 'mask-meeting-name',
        confirmLabel: this.translateService.instant('COMMON.YES'),
      },
    })
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.forceDisableJoiningMutation.mutate(meeting.id)
      }
    })
  }

  get displayDeleteButton(): boolean {
    return this.meeting.type === 'LIVE'
  }

  delete(meeting: Meeting) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('MEETINGS.DELETE.DIALOG_TITLE'),
        description: this.translateService.instant('MEETINGS.DELETE.DIALOG_BODY', { meetingName: meeting.name }),
        descriptionClass: 'mask-meeting-name',
        confirmLabel: this.translateService.instant('COMMON.YES'),
      },
    })
    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        await this.deleteMeetingMutation.mutateAsync(meeting.id)
      }
    })
  }

  onMenuClick(event: Event): boolean {
    event.stopPropagation()
    return false
  }

  async toggleExclude(meetingId: number, checked: boolean): Promise<void> {
    await this.excludeMeetingMutation.mutateAsync({ meetingId, isSetToJoin: checked })
    if (!checked && this.meeting.assistantInstructedToJoinByOtherUsers) {
      this.dialog.open(InfoDialogComponent, {
        width: '400px',
        data: {
          title: this.translateService.instant('MEETINGS.UPCOMING_MEETING.INCLUDED_BY_ANOTHER_USER_DIALOG'),
        },
      })
    }
  }

  get formattedCounterpartyNames(): string {
    return formatCounterpartyNames(this.meeting.counterpartyNames)
  }

  get assistantWillJoinAgainstUserSelection(): boolean {
    return !this.meeting.isSetToJoin && this.meeting.assistantInstructedToJoinByOtherUsers
  }

  get excludeToggleTooltip(): string {
    if (this.assistantWillJoinAgainstUserSelection) {
      return this.translateService.instant('MEETINGS.UPCOMING_MEETING.INCLUDED_BY_ANOTHER_USER')
    }
    if (this.meeting.isSetToJoin) {
      return this.translateService.instant('MEETINGS.UPCOMING_MEETING.EXCLUDE')
    }
    return this.translateService.instant('MEETINGS.UPCOMING_MEETING.INCLUDE')
  }

  onCardClick() {
    this.metricsService.sendMetric(this.meeting.id, MetricType.CLICK_ON_UPCOMING_MEETING)
  }

  get allowJoiningNow(): boolean {
    return differenceInHours(this.meeting.date, new Date()) < 2
  }

  async joinNow() {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('MEETINGS.JOIN_NOW.DIALOG_TITLE'),
        description: this.translateService.instant('MEETINGS.JOIN_NOW.DIALOG_DESCRIPTION'),
      },
    })

    const result = await firstValueFrom(dialogRef.afterClosed())
    if (result) {
      await this.joinNowMutation.mutateAsync(this.meeting.id)
      this.toastService.show('MEETINGS.JOIN_NOW.SUCCESS_CONFIRMATION')
    }
  }
}
